import { sumBy } from 'lodash-es';
import { useMemo } from 'react';
import {
  EventHeaderIconWithHoverEffect,
  EventHeaderMarketplacesWithMetrics,
  EventHeaderMetrics,
  EventHeaderMetricsType,
  EventHeaderSidePanelSwiper,
  EventHeaderStatMetrics,
  EventHeaderSumarryMetrics,
} from 'src/components/Events/EventHeader';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ToggleGroup } from 'src/core/ui/ToggleGroup';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { getCurrentLanguage } from 'src/utils/localeUtils';
import { getInventorySummaryListingMetricsDisplayStrings } from 'src/utils/ticketMetricUtils';
import { Event, Feature, ListingMetrics, Venue } from 'src/WebApiController';

import { InventoryEventTabs } from '../../InventoryEventTabs';
import { useInventoryEventLocalContext } from '../InventoryEventLocalContext';
import { BulkActionToggleButton } from './EventActions/BulkAction';
import { EventContentAction } from './EventActions/ContentAction';
import * as styles from './InventoryEventHeader.css';
import { InventoryEventHeaderNav } from './InventoryEventHeaderNav';

const toggleContentTypes = [
  InventoryEventTabs.Dashboard,
  InventoryEventTabs.Boost,
  InventoryEventTabs.EventDiscovery,
  InventoryEventTabs.PricingABTest,
  InventoryEventTabs.Fulfillment,
  InventoryEventTabs.SellThroughRate,
  InventoryEventTabs.Tags,
];

export const EventHeaderWithNav = ({
  event,
  venue,
}: {
  event: Event;
  venue?: Venue;
}) => {
  const {
    activeTab,
    setActiveTab,
    eventContents,
    marketplaceEventInfo,
    refetchMarketplaceEventInfo,
  } = useInventoryEventLocalContext();
  const { enableSelectionByDefault } = useMultiSelectionContext();
  const isMobile = useMatchMedia('mobile');
  const hasListingTableV2Feature = useUserHasFeature(Feature.ListingTableV2);
  const hasReplaceUnsoldWithAvailableTicketsFeature = useUserHasFeature(
    Feature.ReplaceUnsoldWithAvailableTickets
  );
  const { loginContext } = useAppContext();
  const { eventMetrics: eventMetricsMap } =
    useCatalogMetricsContext<ListingMetrics>();
  const { eventsTransformed } = useCatalogDataContext();

  const catalogMetrics = useMemo(() => {
    const eventListings =
      (eventsTransformed ?? []).find((e) => e.event.virtId === event.virtId)
        ?.entities?.listings ?? [];
    const availableTickets = sumBy(eventListings, (l) => l.availQty ?? 0);

    const quantityNumberFormat = new Intl.NumberFormat(getCurrentLanguage(), {
      style: 'decimal',
      notation: 'compact',
    });
    return {
      formattedTotalAvailableQuantity:
        quantityNumberFormat.format(availableTickets),
    };
  }, [event.virtId, eventsTransformed]);

  const formattedTotalMetrics = useMemo(() => {
    const eventMetrics = eventMetricsMap?.[event?.virtId ?? ''];
    return eventMetrics
      ? getInventorySummaryListingMetricsDisplayStrings(
          eventMetrics,
          loginContext?.user?.activeAccount?.currencyCode
        )
      : undefined;
  }, [
    event?.virtId,
    eventMetricsMap,
    loginContext?.user?.activeAccount?.currencyCode,
  ]);

  const eventToggleContents = useMemo(() => {
    return eventContents
      .filter(({ id }) => toggleContentTypes.includes(id))
      .toSorted(({ id }) => toggleContentTypes.indexOf(id))
      .map(({ id, Icon, contentId, useStroke }) => ({
        value: id,
        children: (
          <EventHeaderIconWithHoverEffect
            IconComponent={Icon}
            selected={id === activeTab}
            useStroke={useStroke}
            contentId={contentId}
            size={vars.iconSize.l}
          />
        ),
      }));
  }, [activeTab, eventContents]);

  const isShowingInventory = useMemo(
    () =>
      activeTab === InventoryEventTabs.Info ||
      activeTab === InventoryEventTabs.ManualPricing,
    [activeTab]
  );

  const metrics = useMemo(():
    | EventHeaderSumarryMetrics
    | EventHeaderStatMetrics => {
    const stats: EventHeaderMetrics[] = [
      {
        label: ContentId.PandL,
        value: formattedTotalMetrics?.formattedProfitAndLossAb ?? 0,
        direction: isMobile ? 'column' : 'row',
        tooltip:
          formattedTotalMetrics?.formattedProfitAndLossAb !==
          formattedTotalMetrics?.formattedProfitAndLoss
            ? formattedTotalMetrics?.formattedProfitAndLoss
            : undefined,
      },
      {
        label: ContentId.UnsoldCost,
        value: formattedTotalMetrics?.formattedTotalUnsoldCostAb ?? 0,
        direction: isMobile ? 'column' : 'row',
        tooltip:
          formattedTotalMetrics?.formattedTotalUnsoldCostAb !==
          formattedTotalMetrics?.formattedTotalUnsoldCost
            ? formattedTotalMetrics?.formattedTotalUnsoldCost
            : undefined,
      },
    ];
    if (hasReplaceUnsoldWithAvailableTicketsFeature) {
      stats.push({
        label: ContentId.TicketsAvailable,
        value: catalogMetrics?.formattedTotalAvailableQuantity ?? 0,
        direction: isMobile ? 'column' : 'row',
      });
    } else {
      stats.push({
        label: ContentId.UnsoldTickets,
        value: formattedTotalMetrics?.formattedTotalUnsoldTicketQuantity ?? 0,
        direction: isMobile ? 'column' : 'row',
      });
    }
    return {
      type: EventHeaderMetricsType.Stats,
      stats,
    };
  }, [
    catalogMetrics?.formattedTotalAvailableQuantity,
    formattedTotalMetrics?.formattedProfitAndLoss,
    formattedTotalMetrics?.formattedProfitAndLossAb,
    formattedTotalMetrics?.formattedTotalUnsoldCost,
    formattedTotalMetrics?.formattedTotalUnsoldCostAb,
    formattedTotalMetrics?.formattedTotalUnsoldTicketQuantity,
    hasReplaceUnsoldWithAvailableTicketsFeature,
    isMobile,
  ]);

  if (isMobile) {
    return (
      <Stack direction="column" gap="m">
        <EventHeaderMarketplacesWithMetrics
          justifyContent="spaceBetween"
          className={styles.eventHeaderNavCard}
          event={event}
          venue={venue}
          marketplaceEventInfo={marketplaceEventInfo}
          onRefetchMarketplaceEventInfo={refetchMarketplaceEventInfo}
          metrics={metrics}
        />
        {isShowingInventory && (
          <Stack
            direction="row"
            justifyContent="spaceBetween"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" gap="m">
              <InventoryEventHeaderNav />
              {event?.virtId &&
                isShowingInventory &&
                !enableSelectionByDefault && (
                  <BulkActionToggleButton groupId={event.virtId} />
                )}
              <EventContentAction />
            </Stack>
            <EventHeaderSidePanelSwiper contentId={ContentId.Map} />
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="responsive"
      alignItems="center"
      justifyContent="spaceBetween"
      className={styles.eventHeaderCard}
    >
      <Stack direction="row" alignItems="center" gap="m">
        <InventoryEventHeaderNav />
        {!enableSelectionByDefault && (
          <BulkActionToggleButton
            groupId={event.virtId}
            show={Boolean(event?.virtId && isShowingInventory)}
          />
        )}
        <ToggleGroup
          value={activeTab}
          itemClassName={styles.toggleGroupItemParent}
          itemSelectedClassName={styles.toggleGroupItemSelected}
          onValueChange={(value) => {
            if (value) {
              setActiveTab(value as InventoryEventTabs);
            }
          }}
          options={eventToggleContents}
        />
        {!hasListingTableV2Feature && <EventContentAction />}
      </Stack>
      <EventHeaderMarketplacesWithMetrics
        gap="m"
        event={event}
        venue={venue}
        marketplaceEventInfo={marketplaceEventInfo}
        onRefetchMarketplaceEventInfo={refetchMarketplaceEventInfo}
        metrics={metrics}
      />
    </Stack>
  );
};
